import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';
import { dashboardKeys } from '@/features/dashboard/hooks/useDashboard';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface CapacityOverview {
  totalAvailableHours: number;
  totalAssignedHours: number;
  utilizationPercent: number;
  staffCount: number;
  overUtilizedCount: number;
  underUtilizedCount: number;
}

export interface StaffAvailabilityEntry {
  userId: string;
  userName: string;
  date: string;
  availableHours: number;
  assignedHours: number;
  absenceType: string | null;
  notes: string | null;
}

export interface StaffMember {
  userId: string;
  userName: string;
  department: string | null;
  totalAvailableHours: number;
  totalAssignedHours: number;
  utilizationPercent: number;
  availability: StaffAvailabilityEntry[];
}

export interface ForecastWeek {
  weekStart: string;
  weekEnd: string;
  availableHours: number;
  assignedHours: number;
  utilizationPercent: number;
}

export interface CapacityForecast {
  weeks: ForecastWeek[];
  periodStart: string;
  periodEnd: string;
}

export interface RebalanceSuggestion {
  id: string;
  jobId: string;
  jobTitle: string;
  fromUserId: string;
  fromUserName: string;
  toUserId: string;
  toUserName: string;
  estimatedHours: number;
  reason: string;
  impact: string;
  status: 'pending' | 'applied' | 'rejected';
  createdAt: string;
}

export interface SetAvailabilityPayload {
  userId: string;
  date: string;
  availableHours: number;
  absenceType?: string;
  notes?: string;
}

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const capacityKeys = {
  all: ['capacity'] as const,
  overview: (teamId?: string) => [...capacityKeys.all, 'overview', teamId] as const,
  forecast: (params?: ForecastParams) => [...capacityKeys.all, 'forecast', params] as const,
  staff: (userId: string) => [...capacityKeys.all, 'staff', userId] as const,
  rebalance: (teamId?: string) => [...capacityKeys.all, 'rebalance', teamId] as const,
};

// ─── Request Types ──────────────────────────────────────────────────────────

export interface ForecastParams {
  periodStart?: string;
  periodEnd?: string;
  weeks?: number;
  teamId?: string;
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * Fetch capacity overview with total available/assigned hours and utilization.
 * Filters staff by team when teamId is provided (from Settings → Data Authorizations → Teams).
 * Requirements: 18.1
 */
export function useCapacityOverview(teamId?: string) {
  // Use same period as the Staff Availability Calendar (next 4 weeks from today, Monday-aligned)
  // so that overview cards and the weekly table show consistent numbers.
  const now = new Date();
  const day = now.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  const monday = new Date(now);
  monday.setDate(monday.getDate() + mondayOffset);
  const startDate = `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;

  const endDateObj = new Date(monday);
  endDateObj.setDate(endDateObj.getDate() + 4 * 7 - 1); // 4 weeks (Sun of last week)
  const endDate = `${endDateObj.getFullYear()}-${String(endDateObj.getMonth() + 1).padStart(2, '0')}-${String(endDateObj.getDate()).padStart(2, '0')}`;

  return useQuery({
    queryKey: capacityKeys.overview(teamId),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<CapacityOverview>>('/capacity/overview', {
        params: { start_date: startDate, end_date: endDate, team_id: teamId || undefined },
      });
      return response.data.data;
    },
    staleTime: 0, // Always refetch on mount (tab switches remount the component)
    refetchOnWindowFocus: true,
  });
}

/**
 * Fetch workload forecast per week for a given period.
 * Filters staff by team when teamId is provided (from Settings → Data Authorizations → Teams).
 * Requirements: 18.2
 */
export function useCapacityForecast(params: ForecastParams = {}) {
  // Align to Monday of the current week so weeks match resource_bookings.week_start
  const now = new Date();
  const day = now.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  const monday = new Date(now);
  monday.setDate(monday.getDate() + mondayOffset);
  const startDate =
    params.periodStart ||
    `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;

  const weeksAhead = params.weeks || 8;
  const endDateObj = new Date(monday);
  endDateObj.setDate(endDateObj.getDate() + weeksAhead * 7 - 1);
  const endDate =
    params.periodEnd ||
    `${endDateObj.getFullYear()}-${String(endDateObj.getMonth() + 1).padStart(2, '0')}-${String(endDateObj.getDate()).padStart(2, '0')}`;

  return useQuery({
    queryKey: capacityKeys.forecast(params),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<CapacityForecast>>('/capacity/forecast', {
        params: { start_date: startDate, end_date: endDate, team_id: params.teamId || undefined },
      });
      return response.data.data;
    },
    staleTime: 0, // Always refetch on mount (tab switches remount the component)
    refetchOnWindowFocus: true,
  });
}

/**
 * Fetch individual staff capacity and availability details.
 * Requirements: 18.1, 18.4
 */
export function useStaffCapacity(userId: string) {
  return useQuery({
    queryKey: capacityKeys.staff(userId),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<StaffMember>>(`/capacity/staff/${userId}`);
      return response.data.data;
    },
    enabled: !!userId,
  });
}

/**
 * Fetch rebalance suggestions generated by the system.
 * Filters by team when teamId is provided (from Settings → Data Authorizations → Teams).
 * Requirements: 18.3
 */
export function useRebalanceSuggestions(teamId?: string, weeks?: number) {
  // Align to Monday of current week — same logic as overview/forecast
  const now = new Date();
  const day = now.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  const monday = new Date(now);
  monday.setDate(monday.getDate() + mondayOffset);
  const startDate = `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;

  const weeksAhead = weeks || 4;
  const endDateObj = new Date(monday);
  endDateObj.setDate(endDateObj.getDate() + weeksAhead * 7 - 1);
  const endDate = `${endDateObj.getFullYear()}-${String(endDateObj.getMonth() + 1).padStart(2, '0')}-${String(endDateObj.getDate()).padStart(2, '0')}`;

  return useQuery({
    queryKey: capacityKeys.rebalance(teamId),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<RebalanceSuggestion[]>>(
        '/capacity/rebalance-suggestions',
        { params: { start_date: startDate, end_date: endDate, team_id: teamId || undefined } },
      );
      return response.data.data;
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

/**
 * Set staff availability for a given date.
 * Requirements: 18.4
 */
export function useSetAvailability() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: SetAvailabilityPayload) => {
      const response = await apiClient.post<ApiResponse<StaffAvailabilityEntry>>(
        '/capacity/availability',
        payload,
      );
      return response.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: capacityKeys.overview() });
      queryClient.invalidateQueries({ queryKey: capacityKeys.staff(variables.userId) });
      queryClient.invalidateQueries({ queryKey: capacityKeys.forecast() });
    },
  });
}

/**
 * Apply a rebalance suggestion (reassign the job).
 * Requirements: 18.3
 */
export function useApplyRebalanceSuggestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { suggestionId: string; jobId: string; toUserId: string }) => {
      const response = await apiClient.post<ApiResponse<RebalanceSuggestion>>(
        `/capacity/rebalance-suggestions/${params.suggestionId}/apply`,
        { job_id: params.jobId, to_user_id: params.toUserId },
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: capacityKeys.rebalance() });
      queryClient.invalidateQueries({ queryKey: capacityKeys.overview() });
      queryClient.invalidateQueries({ queryKey: capacityKeys.forecast() });
      queryClient.invalidateQueries({ queryKey: ['capacity', 'resource-planner'] });
    },
  });
}

// ─── Teams Hook ─────────────────────────────────────────────────────────────

export interface Team {
  id: string;
  name: string;
}

export function useTeams() {
  return useQuery({
    queryKey: ['teams'],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<{ teams: Team[]; meta: unknown }>>('/teams');
      const raw = response.data.data;
      if (raw && 'teams' in raw) return raw.teams;
      if (Array.isArray(raw)) return raw;
      return [];
    },
  });
}

// ─── Resource Planner Hooks ─────────────────────────────────────────────────

export interface ResourceBooking {
  id: string;
  jobId: string;
  jobTitle: string;
  weekStart: string;
  hours: number;
  notes: string | null;
}

export interface ResourcePlannerStaff {
  userId: string;
  userName: string;
  email: string;
  bookings: ResourceBooking[];
}

export interface ResourcePlannerData {
  periodStart: string;
  periodEnd: string;
  staff: ResourcePlannerStaff[];
}

export interface ResourcePlannerParams {
  startDate: string;
  endDate: string;
  teamId?: string;
}

export function useResourcePlanner(params: ResourcePlannerParams) {
  return useQuery({
    queryKey: ['capacity', 'resource-planner', params],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<ResourcePlannerData>>(
        '/capacity/resource-planner',
        {
          params: {
            start_date: params.startDate,
            end_date: params.endDate,
            team_id: params.teamId || undefined,
          },
        },
      );
      return response.data.data;
    },
    enabled: !!params.startDate && !!params.endDate,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

export interface CreateBookingPayload {
  user_id: string;
  job_id: string;
  week_start: string;
  hours: number;
  notes?: string;
}

export function useCreateResourceBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateBookingPayload) => {
      const response = await apiClient.post<ApiResponse<unknown>>(
        '/capacity/resource-bookings',
        payload,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['capacity', 'resource-planner'] });
      queryClient.invalidateQueries({ queryKey: capacityKeys.overview() });
      // Refresh any mounted dashboard widgets (e.g. staff utilization) live.
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
    },
  });
}

export function useDeleteResourceBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (bookingId: string) => {
      await apiClient.delete(`/capacity/resource-bookings/${bookingId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['capacity', 'resource-planner'] });
      queryClient.invalidateQueries({ queryKey: capacityKeys.overview() });
      // Refresh any mounted dashboard widgets (e.g. staff utilization) live.
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
    },
  });
}
